import React, {ComponentProps, useState, forwardRef, useCallback} from 'react'
import styled from 'styled-components'
import Button, {ButtonProps} from '@fuegokit/react/components/Button'
import {useFocusRing} from '@fuegokit/react/hooks'
import Box from '@fuegokit/react/components/Box'
import {FuegokitThemeTypes, themeGet} from '@fuegokit/react/utils/theme-get'
import sx, {SxProp} from '@fuegokit/react/sx'

export type Appearance = 'default' | 'danger' | 'link' | 'primary' | 'subtle' | 'subtle-link' | 'warning'

export type Spacing = 'compact' | 'default' | 'none'

// Similar to {...A, ...B}
// 1. Remove all overlapping types from First
// 2. Add properties from Second
// https://codesandbox.io/s/native-button-with-nested-elementsclick-bnpjg?file=/src/index.ts

/* This type is intentionally not exported to prevent it from being explicitly referenced in the resulting button types. The alternative would
 * be making this public API and re-exporting from the root */

type Combine<First, Second> = Omit<First, keyof Second> & Second

export type StyledAtlasButtonProps = {
  /**
   * Option to fit button width to its parent width
   */
  shouldFitContainer?: boolean
  /**
   * The base styling to apply to the button
   */
  appearance?: Appearance
  /**
   * Set the amount of padding in the button
   */
  spacing?: Spacing
  /**
   * Change the style to indicate the button is selected
   */
  isSelected?: boolean
  /**
   * Set if the button is disabled
   */
  isDisabled?: boolean
  /**
   * Used to 'overlay' something over a button. This is commonly used to display a loading spinner
   */
  overlay?: React.ReactNode
  /**
   * A `testId` prop is provided for specified elements, which is a unique string that appears as a data attribute `data-testid` in the rendered code, serving as a hook for automated tests
   */
  testId?: string
} & ButtonProps

const isFirefox: boolean = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1

type SingleIconButtonProps = Partial<AtlasButtonProps>

/**
 * An empty function which does nothing.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {}

const getIsOnlySingleIcon = ({children, leadingIcon, trailingIcon}: AtlasButtonProps): boolean => {
  if (children) {
    return false
  }
  if (leadingIcon && !trailingIcon) {
    return true
  }
  if (!leadingIcon && trailingIcon) {
    return true
  }
  return false
}

/*
  These Atlassian theme values are expressed as functions so that if we decide to make
  them dependent on props in the future, it wouldn't require a significant
  refactor everywhere they are being used.
*/
export const getBorderRadius = () => 3
export const getGridSize = () => 8
export const getFontSize = () => 14

const borderRadius: number = getBorderRadius()
const gridSize: number = getGridSize()
const fontSize: number = getFontSize()

const heights: {[key in Spacing]: string} = {
  default: `${(gridSize * 4) / fontSize}em`, // 32px
  compact: `${(gridSize * 3) / fontSize}em`,
  none: 'auto'
}
const lineHeights: {[key in Spacing]: string} = {
  default: heights.default,
  compact: heights.compact,
  none: 'inherit'
}
const padding: {[key in Spacing]: string} = {
  // 10px gutter
  default: `0 ${gridSize + gridSize / 4}px`,
  compact: `0 ${gridSize + gridSize / 4}px`,
  none: '0'
}
const verticalAlign: {[key in Spacing]: string} = {
  default: 'middle',
  compact: 'middle',
  none: 'baseline'
}
const innerMargin = {
  content: `0 ${gridSize / 4}px`,
  icon: `0 ${gridSize / 4}px`
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const colors: {[key: string]: any} = {
  background: {
    default: {
      default: 'colors.background.neutral.default.[default]',
      hover: 'colors.background.neutral.default.hovered',
      active: 'colors.background.neutral.default.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    primary: {
      default: 'colors.background.brand.bold.default',
      hover: 'colors.background.brand.bold.hovered',
      active: 'colors.background.brand.bold.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    warning: {
      default: 'colors.background.warning.bold.default',
      hover: 'colors.background.warning.bold.hovered',
      active: 'colors.background.warning.bold.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    danger: {
      default: 'colors.background.danger.bold.default',
      hover: 'colors.background.danger.bold.hovered',
      active: 'colors.background.danger.bold.pressed',
      disabled: 'colors.background.disabled',
      selected: 'colors.background.danger.bold.pressed',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    link: {
      default: 'colors.background.neutral.subtle.default',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    subtle: {
      default: 'colors.background.neutral.subtle.default',
      hover: 'colors.background.neutral.subtle.hovered',
      active: 'colors.background.neutral.subtle.pressed',
      disabled: 'colors.background.neutral.subtle.default',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    },
    'subtle-link': {
      default: 'colors.background.neutral.subtle.default',
      selected: 'colors.background.selected.default.[default]',
      focusSelected: 'colors.background.selected.default.[default]'
    }
  },
  boxShadowColor: {
    default: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    primary: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    warning: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    danger: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    link: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    subtle: {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    },
    'subtle-link': {
      focus: 'colors.border.focused',
      focusSelected: 'colors.border.focused'
    }
  },
  color: {
    default: {
      default: 'colors.text.default',
      hover: 'colors.text.default',
      active: 'colors.text.default',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    primary: {
      default: 'colors.text.inverse',
      hover: 'colors.text.inverse',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    warning: {
      default: 'colors.text.warning.inverse',
      hover: 'colors.text.warning.inverse',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    danger: {
      default: 'colors.text.inverse',
      hover: 'colors.text.inverse',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    link: {
      default: 'colors.link.default',
      hover: 'colors.link.default',
      active: 'colors.link.pressed',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    subtle: {
      default: 'colors.text.default',
      hover: 'colors.text.default',
      active: 'colors.text.default',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    },
    'subtle-link': {
      default: 'colors.text.subtle',
      hover: 'colors.text.subtle',
      active: 'colors.text.default',
      disabled: 'colors.text.disabled',
      selected: 'colors.text.selected',
      focusSelected: 'colors.text.selected'
    }
  }
}

const appearanceObject: {[k: string]: FuegokitThemeTypes} = {
  primary: 'colors.text.inverse',
  danger: 'colors.text.inverse',
  warning: 'colors.text.warning.inverse',
  link: 'colors.link.default',
  subtle: 'colors.text.default',
  'subtle-link': 'colors.text.subtle'
}

const StyledAtlasButton = styled(Button)<StyledAtlasButtonProps & SxProp>`
  // base button styles
  align-items: baseline;
  border-width: 0;
  box-sizing: border-box;
  display: inline-flex; // Margins don't collapse with inline-flex, which helps with preceding/trailing visuals and button content spacing
  font-size: inherit;
  font-style: normal;
  font-weight: 500;
  max-width: 100%;
  position: relative;
  text-align: center;
  text-decoration: none;
  align-items: center;
  transition: background-color 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
  white-space: nowrap;
  user-select: none;

  // dynamic styles
  height: ${p => (p.spacing ? heights[p.spacing] : '2.28571429em')};
  line-height: ${p => (p.spacing ? lineHeights[p.spacing] : '2.28571em')};
  padding: ${p => {
    if (p.spacing && p.isOnlySingleIcon) return '0 4px'
    if (p.spacing) return padding[p.spacing]
    return ''
  }};
  border-radius: ${borderRadius ? `${borderRadius}px` : '3px'};
  vertical-align: ${p => (p.spacing ? verticalAlign[p.spacing] : 'middle')};
  justify-content: ${p => (p.shouldFitContainer === true ? 'center' : '')};
  width: ${p => (p.shouldFitContainer === true ? '100%' : '')};

  // appearances
  background-color: ${p => {
    if (p.appearance && p.isSelected) return themeGet(colors.background[p.appearance].focusSelected)
    if (p.appearance) return themeGet(colors.background[p.appearance].default)
    return themeGet('colors.background.neutral.default.[default]')
  }};
  color: ${p => {
    if (p.appearance && p.isSelected) return themeGet(colors.color[p.appearance].focusSelected)
    if (p.appearance) return themeGet(colors.color[p.appearance].default)
    return themeGet('colors.text.default')
  }};

  &:hover {
    cursor: pointer;
    transition: background-color 0s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
    background-color: ${p => {
      if (p.appearance && p.overlay && p.isSelected) return themeGet(colors.background[p.appearance].focusSelected)
      if (p.appearance && p.overlay) return themeGet(colors.background[p.appearance].default)
      if (p.appearance && p.isSelected) return themeGet(colors.background[p.appearance].focusSelected)
      if (p.appearance) return themeGet(colors.background[p.appearance].hover)
      return themeGet('colors.background.neutral.default.hovered')
    }};
    color: ${p => {
      // Check if the component is an anchor tag
      if (p.appearance && p.as !== 'a') {
        return themeGet(colors.color[p.appearance].hover)
      }
      if (p.appearance && p.as === 'a') {
        return themeGet(colors.color[p.appearance].hover)
      }
      return themeGet('colors.text.default')
    }}!important;
    text-decoration: ${p =>
      !p.isSelected && (p.appearance === 'link' || p.appearance === 'subtle-link') ? 'underline' : 'inherit'};
  }
  &:active {
    outline: none;
    transition-duration: 0s;
    background-color: ${p => {
      if (p.appearance && p.isSelected) return themeGet(colors.background[p.appearance].selected)
      if (p.appearance) return themeGet(colors.background[p.appearance].active)
      return themeGet('colors.background.neutral.default.pressed')
    }};

    color: ${p => {
      if (p.appearance && p.isSelected) return themeGet(colors.color[p.appearance].selected)
      if (p.appearance) return themeGet(colors.color[p.appearance].active)
      return themeGet('colors.background.neutral.default.pressed')
    }};
  }
  &:disabled {
    cursor: not-allowed;
    color: var(--ds-text-disabled, #a5adba) !important;
    background: var(--ds-background-disabled, rgba(9, 30, 66, 0.04));
    text-decoration: none;
  }

  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    background-color: ${p => {
      if (p.appearance && p.isSelected) return themeGet(colors.background[p.appearance].focusSelected)
      if (p.appearance) return themeGet(colors.background[p.appearance].focus)
      return ''
    }};
    box-shadow: 0 0 0 2px
      ${p => {
        if (p.appearance && p.isSelected) return themeGet(colors.boxShadowColor[p.appearance].focus)
        if (p.appearance) return themeGet(colors.boxShadowColor[p.appearance].focus)
        return ''
      }};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px
      ${p => {
        if (p.appearance && p.isSelected) return themeGet(colors.boxShadowColor[p.appearance].focus)
        if (p.appearance) return themeGet(colors.boxShadowColor[p.appearance].focus)
        return ''
      }};
  }
  /**
   * Remove focus styles for non-keyboard focus.
  */
  /* :focus:not(:focus-visible) {
    outline: 0;
    box-shadow: none;
  } */
  &[data-focus-visible-added] {
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${p => (p.appearance ? themeGet(colors.boxShadowColor[p.appearance].focus) : '')};
  }

  &:visited {
    color: ${p =>
      p.appearance && Object.keys(appearanceObject).includes(p.appearance)
        ? themeGet(appearanceObject[p.appearance])
        : themeGet('colors.text.default')};
  }

  &:-moz-focus-inner {
    border: 0;
    margin: 0;
    padding: 0;
  }

  // Remove? this margin property Prevents buttons from working with Stack.
  /* margin: 0; */

  /* Remove? @media (min-width: ${themeGet('breakpoints.0')}) {
    width: revert;
  } */

  /* Remove? Atlaskit injects a deprecated link style for certain anchor tags; 
  this is a safety precaution that won't let that happen for consumers using this component
  who might need to explicitly wrap in AtlaskitThemeprovider and Fuegokit Themeprovider
  */
  --ds-link: ${themeGet('colors.text.default')};
  .light-old-ds-theme-mode a {
    color: var(--ds-link, #172b4d);
  }
  ${sx}
`

/**
 * Use as a drop-in replacement for buttons in Atlassian products.
 *
 * Extends button ARIA primitive. Provides styles for various needs, normalized behavior across different platforms and devices, and consistent keyboard focus behavior.
 *
 */
const AtlasButton = React.memo(
  forwardRef(
    (
      {
        appearance = 'default',
        spacing = 'default',
        shouldFitContainer = false,
        isDisabled,
        isSelected,
        leadingIcon,
        trailingIcon,
        overlay,
        testId,
        children,
        UNSAFE_className,
        href,
        onMouseDown: providedOnMouseDown = noop,
        onMouseUp: providedOnMouseUp = noop,
        as,
        ...props
      }: StyledAtlasButtonProps,
      ref: React.Ref<HTMLElement>
    ) => {
      const appearances = ['default', 'danger', 'link', 'primary', 'subtle', 'subtle-link', 'warning']
      const {isFocusVisible, focusProps} = useFocusRing()

      let Component

      if (appearances.includes(appearance)) {
        Component = StyledAtlasButton
      } else {
        throw new Error(
          `Unrecognized Button appearance: ${appearance}. Please choose from the available options for AtlasButton: "default" | "danger" | "link" | "primary" | "subtle" | "subtle-link" | "warning"`
        )
      }

      const [isActive, setIsActive] = useState<boolean>(false)
      // Wrap onMouseDown / onMouseUp to manually trigger active state in Firefox
      const onMouseUp = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          providedOnMouseUp(event)
          if (isFirefox) {
            setIsActive(false)
          }
        },
        [providedOnMouseUp, setIsActive]
      )

      const onMouseDown = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          providedOnMouseDown(event)
          if (isFirefox) {
            setIsActive(true)
          }
        },
        [providedOnMouseDown, setIsActive]
      )

      const isOnlySingleIcon: boolean = getIsOnlySingleIcon({
        children,
        leadingIcon,
        trailingIcon
      })

      // disable input when there's an overlay, e.g., loading state
      const hasOverlay = Boolean(overlay)
      const isInteractive: boolean = !isDisabled && !hasOverlay

      return (
        <Component
          disabled={isDisabled}
          shouldFitContainer={shouldFitContainer}
          appearance={appearance}
          overlay={overlay}
          spacing={spacing}
          isSelected={isSelected}
          leadingIcon={leadingIcon}
          trailingIcon={trailingIcon}
          isActive={isActive}
          isFocusVisible={isFocusVisible}
          // using undefined so that the property doesn't exist when false
          data-has-overlay={hasOverlay ? true : undefined}
          data-testid={testId}
          // Due to how click events are set, we need to set active styles
          //  manually in Firefox and wrap onMouseDown/onMouseUp
          data-firefox-is-active={isActive ? true : undefined}
          href={isInteractive ? href : undefined}
          className={UNSAFE_className}
          onMouseUp={onMouseUp}
          onMouseDown={onMouseDown}
          isOnlySingleIcon={isOnlySingleIcon}
          as={as}
          ref={ref}
          {...focusProps}
          {...props}
        >
          {overlay && (
            <Box
              as="span"
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {overlay}
            </Box>
          )}
          {overlay ? (
            <Box as="span" sx={{opacity: 0}}>
              {children}
            </Box>
          ) : (
            children
          )}
        </Component>
      )
    }
  )
)

export type ColorGroup = {
  default: string
  hover?: string
  active?: string
  disabled?: string
  selected?: string
  focus?: string
  focusSelected?: string
}

AtlasButton.displayName = 'AtlasButton'
export default AtlasButton
export type AtlasButtonProps = ComponentProps<typeof AtlasButton>
